/**
 * useRealtimeNotifications Hook
 * 
 * SSE (Server-Sent Events) ile gerçek zamanlı bildirimleri dinler
 * ve ActionNotification ile görüntüler.
 * 
 * Kullanım:
 *   import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';
 *   
 *   function App() {
 *     useRealtimeNotifications(); // Hook'u çağır
 *     return <div>...</div>;
 *   }
 */

import logger from '../utils/logger';
import { useEffect, useRef, useCallback, useState } from 'react';
import { useAdminAuth as useAuth } from '../pages/admin/RequireAdminAuth';
import { useActionNotification } from '../components/ActionNotification';

const SSE_ENDPOINT = '/api/notifications/stream';
const RECONNECT_DELAY = 3000; // 3 saniye
const MAX_RECONNECT_ATTEMPTS = 10;

export function useRealtimeNotifications(options = {}) {
    const { status } = useAuth();
    const isAuthenticated = status === 'authenticated';
    const { showNotification } = useActionNotification();
    const eventSourceRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimeoutRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');

    const {
        enableSound = true,
        onNewOrder,
        onLowStock,
        onError,
        onMessage
    } = options;

    // Bildirim sesi çal
    const playSound = useCallback((soundType = 'notification') => {
        if (!enableSound) return;

        try {
            // Ses dosyaları public/sounds/ klasöründe olmalı
            const soundMap = {
                notification: '/sounds/notification.mp3',
                order: '/sounds/order.mp3',
                alert: '/sounds/alert.mp3',
                error: '/sounds/error.mp3'
            };

            const audioSrc = soundMap[soundType] || soundMap.notification;
            const audio = new Audio(audioSrc);
            audio.volume = 0.5;
            audio.play().catch(e => logger.debug('Ses çalınamadı', e, 'SSE'));
        } catch (e) {
            logger.debug('Ses çalma hatası', e, 'SSE');
        }
    }, [enableSound]);

    // SSE mesajını işle
    const handleMessage = useCallback((event) => {
        try {
            if (!event.data || event.data === 'undefined') return;
            const data = JSON.parse(event.data);

            // Custom handler varsa çağır
            if (onMessage) {
                onMessage(data);
            }

            // Event tipine göre işle
            switch (data.type) {
                case 'new_order':
                    showNotification({
                        type: 'order',
                        title: data.data.title || '🎉 Yeni Sipariş!',
                        message: data.data.message,
                        duration: 8000,
                        actions: [
                            {
                                label: 'Siparişe Git',
                                onClick: () => {
                                    window.location.href = `/orders?id=${data.data.orderId || data.data.orderNumber}`;
                                }
                            }
                        ]
                    });
                    playSound('order');
                    if (onNewOrder) onNewOrder(data.data);
                    break;

                case 'low_stock':
                    showNotification({
                        type: 'warning',
                        title: data.data.title || '⚠️ Düşük Stok',
                        message: data.data.message,
                        duration: 6000,
                        actions: [
                            {
                                label: 'Stok Güncelle',
                                onClick: () => {
                                    window.location.href = `/products?sku=${data.data.sku}`;
                                }
                            }
                        ]
                    });
                    playSound('alert');
                    if (onLowStock) onLowStock(data.data);
                    break;

                case 'out_of_stock':
                    showNotification({
                        type: 'error',
                        title: data.data.title || '🚨 Stok Tükendi!',
                        message: data.data.message,
                        duration: 10000,
                        actions: [
                            {
                                label: 'Ürüne Git',
                                onClick: () => {
                                    window.location.href = `/products?sku=${data.data.sku}`;
                                }
                            }
                        ]
                    });
                    playSound('alert');
                    break;

                case 'order_status_change':
                    showNotification({
                        type: 'info',
                        title: data.data.title || '📦 Sipariş Durumu',
                        message: data.data.message,
                        duration: 5000
                    });
                    break;

                case 'new_question':
                    showNotification({
                        type: 'info',
                        title: data.data.title || '❓ Yeni Soru',
                        message: data.data.message,
                        duration: 6000,
                        actions: [
                            {
                                label: 'Yanıtla',
                                onClick: () => {
                                    window.location.href = '/questions';
                                }
                            }
                        ]
                    });
                    playSound('notification');
                    break;

                case 'new_return':
                    showNotification({
                        type: 'warning',
                        title: data.data.title || '↩️ İade Talebi',
                        message: data.data.message,
                        duration: 8000
                    });
                    playSound('alert');
                    break;

                case 'sync_complete':
                    showNotification({
                        type: 'success',
                        title: data.data.title || '✅ Senkronizasyon',
                        message: data.data.message,
                        duration: 4000
                    });
                    break;

                case 'success':
                    showNotification({
                        type: 'success',
                        title: data.data.title,
                        message: data.data.message,
                        duration: 4000
                    });
                    break;

                case 'error':
                    showNotification({
                        type: 'error',
                        title: data.data.title,
                        message: data.data.message,
                        duration: 6000
                    });
                    playSound('error');
                    if (onError) onError(data.data);
                    break;

                case 'warning':
                    showNotification({
                        type: 'warning',
                        title: data.data.title,
                        message: data.data.message,
                        duration: 5000
                    });
                    break;

                case 'info':
                    showNotification({
                        type: 'info',
                        title: data.data.title,
                        message: data.data.message,
                        duration: 5000
                    });
                    break;

                case 'connected':
                    logger.debug('Bağlantı kuruldu', data.data, 'SSE');
                    break;

                default:
                    logger.debug('Bilinmeyen event tipi', data.type, 'SSE');
            }
        } catch (e) {
            console.error('[SSE] Mesaj parse hatası:', e);
        }
    }, [showNotification, playSound, onMessage, onNewOrder, onLowStock, onError]);

    // SSE bağlantısını kur
    const connect = useCallback(() => {
        if (!isAuthenticated) {
            logger.debug('Kullanıcı giriş yapmamış', null, 'SSE');
            return;
        }

        // Zaten bağlıysa yeniden bağlanma
        if (eventSourceRef.current && eventSourceRef.current.readyState === EventSource.OPEN) {
            logger.debug('Zaten bağlı', null, 'SSE');
            return;
        }

        try {
            // Cookie-based auth ile SSE bağlantısı
            const eventSource = new EventSource(SSE_ENDPOINT, { withCredentials: true });
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                logger.debug('Bağlantı açıldı', null, 'SSE');
                setIsConnected(true);
                setConnectionStatus('connected');
                reconnectAttempts.current = 0;
            };

            eventSource.onmessage = handleMessage;

            // Custom event tipleri için listener'lar
            [
                'new_order', 'order_status_change', 'low_stock', 'out_of_stock',
                'new_question', 'new_return', 'sync_complete',
                'success', 'error', 'warning', 'info', 'connected'
            ].forEach(eventType => {
                eventSource.addEventListener(eventType, handleMessage);
            });

            eventSource.onerror = (error) => {
                console.error('[SSE] Bağlantı hatası:', error);
                setIsConnected(false);
                setConnectionStatus('error');

                eventSource.close();
                eventSourceRef.current = null;

                // Yeniden bağlanmayı dene
                if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
                    reconnectAttempts.current++;
                    logger.debug('Yeniden bağlanma denemesi', reconnectAttempts.current, 'SSE');

                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect();
                    }, RECONNECT_DELAY * reconnectAttempts.current);
                } else {
                    console.error('[SSE] Maksimum yeniden bağlanma denemesi aşıldı');
                    setConnectionStatus('failed');
                }
            };

        } catch (error) {
            console.error('[SSE] Bağlantı oluşturma hatası:', error);
            setConnectionStatus('error');
        }
    }, [isAuthenticated, handleMessage]);

    // Bağlantıyı kapat
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        setIsConnected(false);
        setConnectionStatus('disconnected');
        logger.debug('Bağlantı kapatıldı', null, 'SSE');
    }, []);

    // Component mount/unmount
    useEffect(() => {
        if (isAuthenticated) {
            connect();
        }

        return () => {
            disconnect();
        };
    }, [isAuthenticated, connect, disconnect]);

    // Manuel kontrol fonksiyonları
    return {
        isConnected,
        connectionStatus,
        reconnect: connect,
        disconnect
    };
}

export default useRealtimeNotifications;
